import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { getProject, getTasks, getProjects, createTask, deleteTask, updateTask, getNotes, createNote, analyzeTask, chatWithAgent } from '../api';
import { usePermissions } from '../context/PermissionsContext';
import { useAuth } from '../context/AuthContext';
import { getScheduleHealth, HEALTH } from '../utils/scheduleHealth';
import UserSelector from '../components/UserSelector';
import FileUploader from '../components/FileUploader';
import TaskDetailModal from '../components/TaskDetailModal';
import { decodeEntities } from '../utils/decodeEntities';

if (typeof document !== 'undefined' && !document.getElementById('gpf-progress-slider-style')) {
  const style = document.createElement('style');
  style.id = 'gpf-progress-slider-style';
  style.textContent = `
    .gpf-progress-slider::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 14px; height: 14px; border-radius: 50%;
      background: white; border: 2px solid #2563eb; cursor: pointer;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    }
    .gpf-progress-slider::-moz-range-thumb {
      width: 14px; height: 14px; border-radius: 50%;
      background: white; border: 2px solid #2563eb; cursor: pointer;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    }
    .gpf-progress-slider::-moz-range-track { background: transparent; }
  `;
  document.head.appendChild(style);
}

const PRIORITIES = ['low', 'medium', 'high'];
const STATUSES   = ['not_started', 'in_progress', 'completed', 'overdue'];
const emptyTaskForm = { title: '', description: '', status: 'not_started', priority: 'medium', progress: 0, start_date: '', end_date: '', assigned_users: [], project_id: '' };
const emptyNoteForm = { title: '', content: '', hours: 0 };

export default function TasksPage() {
  const { projectId } = useParams();
  const navigate      = useNavigate();
  const location      = useLocation();
  const [project,      setProject]      = useState(null);
  const [tasks,        setTasks]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showForm,     setShowForm]     = useState(false);
  const [taskForm,     setTaskForm]     = useState(emptyTaskForm);
  const [saving,       setSaving]       = useState(false);
  const [filter,       setFilter]       = useState('all');
  const [healthFilter, setHealthFilter] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailTask,   setDetailTask]   = useState(null);
  const [notes,        setNotes]        = useState([]);
  const [noteForm,     setNoteForm]     = useState(emptyNoteForm);
  const [savingNote,   setSavingNote]   = useState(false);
  const [editTask,     setEditTask]     = useState(null);
  const [editTaskForm, setEditTaskForm] = useState(emptyTaskForm);
  const [allProjects,  setAllProjects]  = useState([]);
  const [aiAnalysis,   setAiAnalysis]   = useState('');
  const [aiLoading,    setAiLoading]    = useState(false);
  const [aiQuestion,   setAiQuestion]   = useState('');
  const [aiChat,       setAiChat]       = useState([]);
  const [taskError,    setTaskError]    = useState('');

  const today       = new Date().toISOString().split('T')[0];
  const getAttachCount = (task) => {
    try { return JSON.parse(task.meta?.gpf_file_ids || '[]').length; }
    catch { return 0; }
  };
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => { const h = () => setIsMobile(window.innerWidth <= 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  const currentUser = JSON.parse(localStorage.getItem('gpf_user') || '{}');
  const { can } = usePermissions();
  const [timerElapsed, setTimerElapsed] = useState(0);
  const [timerPaused,  setTimerPaused]  = useState(false);
  const timerRef    = React.useRef(null);
  const timerStart  = React.useRef(null);
  const pausedAt    = React.useRef(null);

  // Start/stop timer when task selection changes
  useEffect(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (selectedTask?.id) {
      timerStart.current = Date.now();
      setTimerElapsed(0); setTimerPaused(false);
      timerRef.current = setInterval(() => {
        setTimerElapsed(Math.floor((Date.now() - timerStart.current) / 1000));
      }, 1000);
    } else {
      timerStart.current = null; setTimerElapsed(0); setTimerPaused(false);
    }
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  }, [selectedTask?.id]);

  const pauseTimer = () => {
    if (timerPaused || !timerRef.current) return;
    clearInterval(timerRef.current); timerRef.current = null;
    pausedAt.current = Date.now(); setTimerPaused(true);
  };
  const resumeTimer = () => {
    if (!timerPaused) return;
    if (pausedAt.current) timerStart.current += (Date.now() - pausedAt.current);
    pausedAt.current = null; setTimerPaused(false);
    timerRef.current = setInterval(() => {
      setTimerElapsed(Math.floor((Date.now() - timerStart.current) / 1000));
    }, 1000);
  };
  const stopAndGetHours = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (!timerStart.current) return 0;
    const secs = timerPaused && pausedAt.current
      ? (pausedAt.current - timerStart.current) / 1000
      : (Date.now() - timerStart.current) / 1000;
    timerStart.current = null;
    return Math.round((secs / 3600) * 4) / 4;
  };
  const h = Math.floor(timerElapsed / 3600);
  const m = Math.floor((timerElapsed % 3600) / 60);
  const s = timerElapsed % 60;
  const formattedTime = h > 0 ? h + 'h ' + String(m).padStart(2,'0') + 'm' : String(m).padStart(2,'0') + ':' + String(s).padStart(2,'0');

  const fetchAll = async () => {
    try {
      const [pRes, tRes] = await Promise.all([ getProject(projectId), getTasks(projectId) ]);
      setProject(pRes.data);
      setTasks(tRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // Lighter refresh for actions that only change tasks, not the project itself
  const fetchTasksOnly = async () => {
    try {
      const tRes = await getTasks(projectId);
      setTasks(tRes.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    fetchAll();
    getProjects().then(res => setAllProjects(res.data)).catch(console.error);
  }, [projectId]);

  // Auto-open task from URL param e.g. ?task=123 — only once per URL, not on every tasks refresh
  const autoOpenedRef = React.useRef('');
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskId = params.get('task');
    const editMode = params.get('edit') === '1';
    const key = `${taskId}-${editMode}`;
    if (taskId && tasks.length > 0 && autoOpenedRef.current !== key) {
      const found = tasks.find(t => t.id === parseInt(taskId));
      if (found) {
        autoOpenedRef.current = key;
        if (editMode) {
          handleEditTask(found, null);
        } else {
          setSelectedTask(found);
        }
        // Clear the URL params so this doesn't re-trigger on future state updates
        navigate(location.pathname, { replace: true });
      }
    }
  }, [tasks, location.search]);

  useEffect(() => {
    setAiAnalysis(''); setAiChat([]); setAiQuestion('');
  }, [selectedTask?.id]);

  useEffect(() => {
    if (!selectedTask) return;
    getNotes(selectedTask.id).then(res => setNotes(res.data)).catch(console.error);
  }, [selectedTask]);

  const handleEditTask = (task, e) => {
    if (e) e.stopPropagation();
    setEditTask(task);
    setEditTaskForm({
      title: decodeEntities(task.title?.rendered) || '', description: decodeEntities(task.content?.rendered)?.replace(/<[^>]+>/g, '') || '',
      status: task.meta?.gpf_status || 'not_started', priority: task.meta?.gpf_priority || 'medium',
      progress: task.meta?.gpf_progress || 0, start_date: task.meta?.gpf_start_date || '', end_date: task.meta?.gpf_end_date || '',
      project_id: task.meta?.gpf_project_id || parseInt(projectId),
      assigned_users: (() => { try { return JSON.parse(task.meta?.gpf_assigned_users || '[]'); } catch { return []; } })(),
    });
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const wasStarted = editTask.meta?.gpf_started_by;
      const isStartingNow = editTaskForm.status === 'in_progress' && !wasStarted;
      const updateData = { ...editTaskForm, project_id: parseInt(editTaskForm.project_id) || parseInt(projectId) };
      if (isStartingNow) { updateData.started_by = currentUser.name || 'Unknown'; updateData.started_date = today; }
      await updateTask(editTask.id, updateData);
      // Optimistic local update — reflect the edit instantly instead of a full refetch
      setTasks(prev => prev.map(t => t.id !== editTask.id ? t : {
        ...t,
        title:   { rendered: editTaskForm.title },
        content: { rendered: editTaskForm.description },
        meta: {
          ...t.meta,
          gpf_status:       editTaskForm.status,
          gpf_priority:     editTaskForm.priority,
          gpf_progress:     editTaskForm.progress,
          gpf_start_date:   editTaskForm.start_date,
          gpf_end_date:     editTaskForm.end_date,
          gpf_project_id:   updateData.project_id,
          gpf_started_by:   isStartingNow ? (currentUser.name || 'Unknown') : (t.meta?.gpf_started_by || ''),
          gpf_started_date: isStartingNow ? today : (t.meta?.gpf_started_date || ''),
          gpf_assigned_users: JSON.stringify(editTaskForm.assigned_users || []),
        },
      }));
      setEditTask(null);
    } catch (err) { console.error(err); fetchAll(); } finally { setSaving(false); }
  };

  const handleDuplicateTask = async (task, e) => {
    e.stopPropagation();
    try {
      await createTask({ title: 'COPY — ' + (decodeEntities(task.title?.rendered) || 'Untitled'), description: decodeEntities(task.content?.rendered)?.replace(/<[^>]+>/g, '') || '', project_id: parseInt(projectId), status: 'not_started', priority: task.meta?.gpf_priority || 'medium', progress: 0, start_date: task.meta?.gpf_start_date || '', end_date: task.meta?.gpf_end_date || '', assigned_users: JSON.parse(task.meta?.gpf_assigned_users || '[]') });
      fetchTasksOnly();
    } catch (err) { console.error(err); }
  };

  const handleAiAnalyze = async () => {
    if (!selectedTask || !project) return;
    setAiLoading(true);
    try {
      const res = await analyzeTask({ task_id: selectedTask.id, title: decodeEntities(selectedTask.title?.rendered), description: decodeEntities(selectedTask.content?.rendered)?.replace(/<[^>]+>/g, '') || '', project_name: decodeEntities(project.title?.rendered), priority: selectedTask.meta?.gpf_priority || 'medium', due_date: selectedTask.meta?.gpf_end_date || 'Not set' });
      setAiAnalysis(res.data.analysis);
    } catch (err) { setAiAnalysis('Failed to get AI analysis. Please check your API key in Settings.'); }
    finally { setAiLoading(false); }
  };

  const handleAiChat = async (e) => {
    e.preventDefault();
    if (!aiQuestion.trim() || !selectedTask) return;
    const question = aiQuestion;
    setAiQuestion('');
    setAiChat(prev => [...prev, { role: 'user', text: question }]);
    setAiLoading(true);
    try {
      const res = await chatWithAgent(question, { task_id: selectedTask.id, title: decodeEntities(selectedTask.title?.rendered), description: decodeEntities(selectedTask.content?.rendered)?.replace(/<[^>]+>/g, '') || '', project_name: project?.title?.rendered, priority: selectedTask.meta?.gpf_priority || 'medium', progress: selectedTask.meta?.gpf_progress || 0, due_date: selectedTask.meta?.gpf_end_date || 'Not set' });
      setAiChat(prev => [...prev, { role: 'ai', text: res.data.response }]);
    } catch (err) { setAiChat(prev => [...prev, { role: 'ai', text: 'Sorry, I encountered an error. Please try again.' }]); }
    finally { setAiLoading(false); }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setTaskError('');
    if (!taskForm.start_date) { setTaskError('Start date is required.'); return; }
    if (!taskForm.end_date)   { setTaskError('End date is required.'); return; }
    if (taskForm.end_date < taskForm.start_date) { setTaskError('End date must be after the start date.'); return; }
    if (!taskForm.assigned_users?.length) { setTaskError('Please assign at least one user.'); return; }
    if (project?.meta?.gpf_start_date && taskForm.start_date < project.meta.gpf_start_date) {
      setTaskError(`Task start date cannot be before the project start date (${project.meta.gpf_start_date}).`); return;
    }
    setSaving(true);
    try { await createTask({ ...taskForm, project_id: parseInt(taskForm.project_id) || parseInt(projectId) }); setTaskForm(emptyTaskForm); setShowForm(false); fetchTasksOnly(); }
    catch (err) { console.error(err); } finally { setSaving(false); }
  };

  const handleDeleteTask = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    await deleteTask(id);
    setTasks(tasks.filter(t => t.id !== id));
    if (selectedTask?.id === id) setSelectedTask(null);
  };

  const handleProgressChange = async (task, newProgress) => {
    const wasStarted = task.meta?.gpf_started_by;
    const newStatus  = newProgress === 100 ? 'completed' : newProgress > 0 ? 'in_progress' : task.meta?.gpf_status;
    const isStartingNow = newProgress > 0 && !wasStarted;

    // Optimistic update — reflect the change instantly, no waiting on the network
    setTasks(prev => prev.map(t => t.id !== task.id ? t : {
      ...t,
      meta: {
        ...t.meta,
        gpf_progress:     newProgress,
        gpf_status:       newStatus,
        gpf_started_by:   isStartingNow ? (currentUser.name || 'Unknown') : (t.meta?.gpf_started_by || ''),
        gpf_started_date: isStartingNow ? today : (t.meta?.gpf_started_date || ''),
      },
    }));

    try {
      await updateTask(task.id, { title: decodeEntities(task.title?.rendered), description: decodeEntities(task.content?.rendered), project_id: parseInt(projectId), status: newStatus, priority: task.meta?.gpf_priority, progress: newProgress, start_date: task.meta?.gpf_start_date, end_date: task.meta?.gpf_end_date, hours: task.meta?.gpf_hours, started_by: isStartingNow ? (currentUser.name || 'Unknown') : (task.meta?.gpf_started_by || ''), started_date: isStartingNow ? today : (task.meta?.gpf_started_date || '') });
      // No fetchAll() here — local state already reflects the change, saving 2 round trips
    } catch (err) {
      console.error(err);
      fetchAll(); // only resync from server if the save actually failed
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault(); setSavingNote(true);
    try {
      await createNote({ ...noteForm, task_id: selectedTask.id, project_id: parseInt(projectId), author_id: currentUser.id || 0, date: today });
      setNoteForm(emptyNoteForm);
      const res = await getNotes(selectedTask.id); setNotes(res.data);
    } catch (err) { console.error(err); } finally { setSavingNote(false); }
  };

  const getEffectiveTaskStatus = (t) => {
    const status = t.meta?.gpf_status || 'not_started';
    const endDate = t.meta?.gpf_end_date;
    if (status !== 'completed' && endDate && endDate < today) return 'overdue';
    return status;
  };

  const filtered = tasks.filter(t => {
    const effective = getEffectiveTaskStatus(t);
    let matchesTab = true;
    if (filter === 'not_started') matchesTab = effective === 'not_started';
    else if (filter === 'in_progress') matchesTab = effective === 'in_progress';
    else if (filter === 'completed')   matchesTab = effective === 'completed';
    else if (filter === 'overdue')     matchesTab = effective === 'overdue';
    if (!matchesTab) return false;

    if (healthFilter) {
      const progress = parseInt(t.meta?.gpf_progress || 0);
      const health = getScheduleHealth(t.meta?.gpf_start_date || '', t.meta?.gpf_end_date || '', progress, t.meta?.gpf_status || 'not_started');
      if (health.label !== healthFilter) return false;
    }
    return true;
  });

  const priorityStyle = (p) => ({ high: { bg: '#fef2f2', text: '#dc2626' }, medium: { bg: '#fffbeb', text: '#d97706' }, low: { bg: '#f0fdf4', text: '#16a34a' } }[p] || { bg: '#fffbeb', text: '#d97706' });
  const statusLabel   = (s) => ({ not_started: 'Not Started', in_progress: 'In Progress', completed: 'Completed', overdue: 'Overdue' }[s] || s);

  if (loading) return <Layout><p style={{ color: '#64748b', padding: 32 }}>Loading...</p></Layout>;

  return (
    <Layout>
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <button style={styles.backBtn} onClick={() => navigate('/projects')}>← Projects</button>
            <h1 style={styles.title}>{project?.title?.rendered || 'Project Tasks'}</h1>
            <p style={styles.subtitle}>{tasks.length} task{tasks.length !== 1 ? 's' : ''}</p>
          </div>
          {can('projects', 'add') && <button style={styles.primaryBtn} onClick={() => setShowForm(true)}>+ Add Task</button>}
        </div>

        <div style={styles.tabs}>
          {['all', 'not_started', 'in_progress', 'overdue', 'completed'].map(f => (
            <button key={f} style={{ ...styles.tab, ...(filter === f ? styles.tabActive : {}) }} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : statusLabel(f)}
            </button>
          ))}
        </div>

        <div style={styles.body}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {showForm && (
              <div style={styles.formCard}>
                <h2 style={styles.formTitle}>New Task</h2>
                <form onSubmit={handleCreateTask} style={styles.form}>
                  <div style={styles.formGrid}>
                    <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                      <label style={styles.label}>Task Name *</label>
                      <input style={styles.input} value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="What needs to be done?" required />
                    </div>
                    <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                      <label style={styles.label}>Description</label>
                      <textarea style={{ ...styles.input, height: 70, resize: 'vertical' }} value={taskForm.description} onChange={e => setTaskForm({ ...taskForm, description: e.target.value })} placeholder="Optional details..." />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Status</label>
                      <select style={styles.input} value={taskForm.status} onChange={e => setTaskForm({ ...taskForm, status: e.target.value })}>
                        {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Priority</label>
                      <select style={styles.input} value={taskForm.priority} onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}>
                        {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Start Date</label>
                      <input style={styles.input} type="date" value={taskForm.start_date} onChange={e => setTaskForm({ ...taskForm, start_date: e.target.value })} />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Due Date</label>
                      <input style={styles.input} type="date" value={taskForm.end_date} onChange={e => setTaskForm({ ...taskForm, end_date: e.target.value })} />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Progress (%)</label>
                      <input style={styles.input} type="number" min="0" max="100" value={taskForm.progress} onChange={e => setTaskForm({ ...taskForm, progress: parseInt(e.target.value) || 0 })} />
                    </div>
                    <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                      <label style={styles.label}>Project</label>
                      <select style={styles.input} value={taskForm.project_id || projectId} onChange={e => setTaskForm({ ...taskForm, project_id: e.target.value })}>
                        {allProjects.map(p => <option key={p.id} value={p.id}>{decodeEntities(p.title?.rendered)?.toUpperCase()}</option>)}
                      </select>
                    </div>
                    <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                      <label style={styles.label}>Assigned Users</label>
                      <UserSelector selected={taskForm.assigned_users || []} onChange={val => setTaskForm({ ...taskForm, assigned_users: val })} />
                    </div>
                  </div>
                  {taskError && <p style={{ color: '#ef4444', fontSize: 13, margin: 0 }}>{taskError}</p>}
                  <div style={styles.formActions}>
                    <button type="button" style={styles.cancelBtn} onClick={() => { setShowForm(false); setTaskForm(emptyTaskForm); setTaskError(''); }}>Cancel</button>
                    <button type="submit" style={styles.primaryBtn} disabled={saving}>{saving ? 'Creating...' : 'Create Task'}</button>
                  </div>
                </form>
              </div>
            )}

            {filtered.length === 0 ? (
              <div style={styles.empty}><p style={styles.emptyText}>{filter === 'all' ? 'No tasks yet. Add your first one!' : `No ${statusLabel(filter).toLowerCase()} tasks.`}</p></div>
            ) : (
              <div style={styles.taskList}>
                {filtered.map(task => {
                  const status    = task.meta?.gpf_status    || 'not_started';
                  const priority  = task.meta?.gpf_priority  || 'medium';
                  const progress  = task.meta?.gpf_progress  || 0;
                  const health    = getScheduleHealth(task.meta?.gpf_start_date || '', task.meta?.gpf_end_date || '', progress, status);
                  const pc        = priorityStyle(priority);
                  const isSelected = selectedTask?.id === task.id;
                  const isDone    = status === 'completed' || progress === 100;
                  return (
                    <div
                      key={task.id}
                      style={{
                        ...styles.taskRow,
                        borderLeft: `4px solid ${health.border}`,
                        background: isSelected ? '#eff6ff' : (() => {
                          if (isDone) return '#f8fafc';
                          switch (health.border) {
                            case '#dc2626': return '#fff5f5';
                            case '#d97706': return '#fffbf0';
                            case '#16a34a': return '#f0fdf4';
                            default:        return '#fff';
                          }
                        })(),
                      }}
                      onClick={() => setSelectedTask(isSelected ? null : task)}
                    >
                      <div style={styles.taskMain}>
                        <div style={styles.taskTop}>
                          <span
                            style={{ ...styles.taskName, textDecoration: isDone ? 'line-through' : 'none', opacity: isDone ? 0.5 : 1, cursor: 'pointer' }}
                            onClick={e => { e.stopPropagation(); setDetailTask(task); }}
                            title="Click to view task summary"
                          >{decodeEntities(task.title?.rendered) || 'Untitled'}</span>
                          {getAttachCount(task) > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 99, background: '#fef3c7', color: '#d97706', flexShrink: 0 }} title={`${getAttachCount(task)} attachment(s)`}>
                              📎 {getAttachCount(task)}
                            </span>
                          )}
                          <div style={styles.taskBadges}>
                            <span style={{ ...styles.badge, background: pc.bg, color: pc.text }}>{priority}</span>
                            <span style={{ ...styles.healthDot, background: health.border }} title={health.label} />
                          </div>
                        </div>
                        {task.meta?.gpf_started_by && (
                          <div style={styles.startedBy}>🚀 Created by <strong>{task.meta.gpf_started_by}</strong>{task.meta?.gpf_started_date && <span> on {task.meta.gpf_started_date}</span>}</div>
                        )}
                        <div style={styles.taskBottom} onClick={e => e.stopPropagation()}>
                          <input
                            type="range" min="0" max="100" defaultValue={progress}
                            className="gpf-progress-slider"
                            style={{
                              ...styles.progressSlider,
                              background: `linear-gradient(to right, ${health.border} 0%, ${health.border} ${progress}%, #e2e8f0 ${progress}%, #e2e8f0 100%)`,
                            }}
                            onMouseUp={e => handleProgressChange(task, parseInt(e.target.value))}
                            onTouchEnd={e => handleProgressChange(task, parseInt(e.target.value))}
                          />
                          <span style={styles.progressPct}>{progress}%</span>
                          {task.meta?.gpf_end_date && <span style={{ ...styles.dueDate, color: health.border }}>Due {task.meta.gpf_end_date}</span>}
                        </div>
                      </div>
                      <div style={styles.assignedAvatars}>
                        {(() => {
                          const assignedCount = JSON.parse(task.meta?.gpf_assigned_users || '[]').length;
                          return assignedCount > 0 ? (
                            <span style={styles.assignedBadge}>👤 {assignedCount}</span>
                          ) : (
                            <span style={{ fontSize: 11, color: '#cbd5e1' }}>—</span>
                          );
                        })()}
                      </div>
                      <div style={styles.taskActions} onClick={e => e.stopPropagation()}>
                        <button style={{ ...styles.iconBtn, color: '#7c3aed' }} onClick={(e) => { e.stopPropagation(); setSelectedTask(isSelected ? null : task); }} title="Notes">📝</button>
                        {can('projects', 'edit') && <button style={{ ...styles.iconBtn, color: '#2563eb' }} onClick={(e) => handleEditTask(task, e)} title="Edit">✎</button>}
                        {can('projects', 'add') && <button style={{ ...styles.iconBtn, color: '#16a34a' }} onClick={(e) => handleDuplicateTask(task, e)} title="Duplicate">⧉</button>}
                        {can('projects', 'delete') && <button style={{ ...styles.iconBtn, color: '#ef4444' }} onClick={() => handleDeleteTask(task.id, decodeEntities(task.title?.rendered))} title="Delete">✕</button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Notes panel ── */}
          {selectedTask && (
            <div style={styles.notesPanel}>
              <div style={styles.notesPanelHeader}>
                <div>
                  <h3 style={styles.notesPanelTitle}>Notes</h3>
                  <p style={styles.notesPanelSub}>{decodeEntities(selectedTask.title?.rendered)}</p>
                </div>
                <div style={styles.timerBox}>
                  <span style={{ ...styles.timerDot, background: timerPaused ? '#d97706' : '#16a34a' }} />
                  <span style={styles.timerText}>{formattedTime}</span>
                  <button style={{ background:'none', border:'none', cursor:'pointer', fontSize:11, padding:'0 4px', color: timerPaused ? '#16a34a' : '#d97706' }} onClick={timerPaused ? resumeTimer : pauseTimer} title={timerPaused ? 'Resume' : 'Pause'}>{timerPaused ? '▶' : '⏸'}</button>
                </div>
                <button style={styles.closeBtn} onClick={async () => {
                  const hours = stopAndGetHours();
                  if (hours >= 0.25) {
                    try {
                      await createNote({ content: `Auto-tracked: ${hours}h spent on this task`, task_id: selectedTask.id, project_id: parseInt(projectId), author_id: currentUser.id || 0, hours, date: today });
                      const res = await getNotes(selectedTask.id); setNotes(res.data);
                    } catch(e) { console.error(e); }
                  }
                  setSelectedTask(null);
                }}>✕</button>
              </div>

              {/* Notes list */}
              <div style={styles.notesList}>
                {notes.length === 0 ? <p style={styles.noNotes}>No notes yet.</p> : (
                  notes.map(note => (
                    <div key={note.id} style={styles.noteItem}>
                      <div style={styles.noteAuthorRow}>
                        <span style={styles.noteAuthor}>{note.gpf_author_name || 'Unknown'}</span>
                        <span style={styles.noteDate}>{note.meta?.gpf_date || ''}</span>
                      </div>
                      {note.meta?.gpf_hours > 0 && <span style={styles.noteHours}>{note.meta.gpf_hours}h logged</span>}
                      <p style={styles.noteContent} dangerouslySetInnerHTML={{ __html: decodeEntities(note.content?.rendered) || '' }} />
                    </div>
                  ))
                )}
              </div>

              {/* Add note form */}
              {can('notes', 'add') && <form onSubmit={handleAddNote} style={styles.noteForm}>
                <h4 style={styles.addNoteTitle}>Add Note</h4>
                <textarea style={{ ...styles.input, height: 80, resize: 'vertical' }} placeholder="Write a note..." value={noteForm.content} onChange={e => setNoteForm({ ...noteForm, content: e.target.value })} required />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input style={{ ...styles.input, width: 80 }} type="number" min="0" step="0.25" placeholder="Hours" value={noteForm.hours || ''} onChange={e => setNoteForm({ ...noteForm, hours: parseFloat(e.target.value) || 0 })} />
                  <span style={styles.hoursLabel}>hours logged</span>
                  <button type="submit" style={{ ...styles.primaryBtn, marginLeft: 'auto' }} disabled={savingNote}>{savingNote ? 'Saving...' : 'Save Note'}</button>
                </div>
              </form>}

              {/* Files */}
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                <FileUploader taskId={selectedTask.id} existingIds={JSON.parse(selectedTask.meta?.gpf_file_ids || '[]')} onUpdate={fetchAll} />
              </div>

              {/* AI Agent Panel */}
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                <div style={styles.aiHeader}>
                  <span style={styles.aiTitle}>🤖 AI Agent</span>
                  {!aiAnalysis && (
                    <button style={styles.aiAnalyzeBtn} onClick={handleAiAnalyze} disabled={aiLoading}>
                      {aiLoading ? 'Analyzing...' : 'Analyze Task'}
                    </button>
                  )}
                </div>
                {aiAnalysis && (
                  <div style={styles.aiAnalysis}>
                    <pre style={styles.aiAnalysisText}>{aiAnalysis}</pre>
                  </div>
                )}
                {(aiAnalysis || aiChat.length > 0) && (
                  <div style={styles.aiChat}>
                    {aiChat.map((msg, i) => (
                      <div key={i} style={{ ...styles.aiChatMsg, alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', background: msg.role === 'user' ? '#2563eb' : '#f1f5f9', color: msg.role === 'user' ? '#fff' : '#0f172a' }}>
                        {msg.text}
                      </div>
                    ))}
                    {aiLoading && <div style={{ ...styles.aiChatMsg, alignSelf: 'flex-start', background: '#f1f5f9', color: '#64748b' }}>🤖 Thinking...</div>}
                  </div>
                )}
                {aiAnalysis && (
                  <form onSubmit={handleAiChat} style={styles.aiQuestionForm}>
                    <input style={{ ...styles.input, flex: 1, fontSize: 12 }} placeholder="Ask the AI agent anything about this task..." value={aiQuestion} onChange={e => setAiQuestion(e.target.value)} disabled={aiLoading} />
                    <button type="submit" style={styles.aiSendBtn} disabled={aiLoading || !aiQuestion.trim()}>→</button>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Color legend — click to filter by schedule health */}
        <div style={styles.legend}>
          {Object.values(HEALTH).map(({ color, bg, label }) => {
            const active = healthFilter === label;
            return (
              <button
                key={label}
                style={{
                  ...styles.legendItem,
                  background: active ? bg : 'transparent',
                  border: active ? `1.5px solid ${color}` : '1.5px solid transparent',
                }}
                onClick={() => setHealthFilter(active ? null : label)}
                title={active ? 'Click to clear filter' : `Show only "${label}" items`}
              >
                <span style={{ ...styles.legendDot, background: color }} />
                <span style={{ ...styles.legendLabel, color: active ? color : styles.legendLabel.color, fontWeight: active ? 700 : 500 }}>{label}</span>
              </button>
            );
          })}
          {healthFilter && (
            <button style={styles.legendClear} onClick={() => setHealthFilter(null)}>✕ Clear</button>
          )}
        </div>
      </div>

      {/* Edit Task Modal */}
      {editTask && (
        <div style={styles.modalOverlay} onClick={() => setEditTask(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 }}>Edit Task</h2>
              <button style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#94a3b8' }} onClick={() => setEditTask(null)}>✕</button>
            </div>
            <form onSubmit={handleUpdateTask} style={styles.form}>
              <div style={styles.formGrid}>
                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Task Name *</label>
                  <input style={styles.input} value={editTaskForm.title} onChange={e => setEditTaskForm({ ...editTaskForm, title: e.target.value })} required />
                </div>
                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Description</label>
                  <textarea style={{ ...styles.input, height: 70, resize: 'vertical' }} value={editTaskForm.description} onChange={e => setEditTaskForm({ ...editTaskForm, description: e.target.value })} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Status</label>
                  <select style={styles.input} value={editTaskForm.status} onChange={e => setEditTaskForm({ ...editTaskForm, status: e.target.value })}>
                    {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Priority</label>
                  <select style={styles.input} value={editTaskForm.priority} onChange={e => setEditTaskForm({ ...editTaskForm, priority: e.target.value })}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Start Date</label>
                  <input style={styles.input} type="date" value={editTaskForm.start_date} onChange={e => setEditTaskForm({ ...editTaskForm, start_date: e.target.value })} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Due Date</label>
                  <input style={styles.input} type="date" value={editTaskForm.end_date} onChange={e => setEditTaskForm({ ...editTaskForm, end_date: e.target.value })} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Progress (%)</label>
                  <input style={styles.input} type="number" min="0" max="100" value={editTaskForm.progress} onChange={e => setEditTaskForm({ ...editTaskForm, progress: parseInt(e.target.value) || 0 })} />
                </div>
                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Project</label>
                  <select style={styles.input} value={editTaskForm.project_id || projectId} onChange={e => setEditTaskForm({ ...editTaskForm, project_id: e.target.value })}>
                    {allProjects.map(p => <option key={p.id} value={p.id}>{decodeEntities(p.title?.rendered)?.toUpperCase()}</option>)}
                  </select>
                </div>
                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Assigned To</label>
                  <UserSelector selected={editTaskForm.assigned_users || []} onChange={val => setEditTaskForm({ ...editTaskForm, assigned_users: val })} />
                </div>
              </div>
              <div style={styles.formActions}>
                <button type="button" style={styles.cancelBtn} onClick={() => setEditTask(null)}>Cancel</button>
                <button type="submit" style={styles.primaryBtn} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {detailTask && (
        <TaskDetailModal
          task={detailTask}
          onClose={() => setDetailTask(null)}
          onEdit={(t) => { setDetailTask(null); setSelectedTask(t); handleEditTask(t, null); }}
          onOpenNotes={(t) => { setDetailTask(null); setSelectedTask(t); }}
        />
      )}
    </Layout>
  );
}

const styles = {
  page:           { maxWidth: 1100 },
  header:         { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  backBtn:        { background: 'none', border: 'none', color: '#2563eb', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 6, display: 'block' },
  title:          { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle:       { color: '#64748b', fontSize: 14, margin: '4px 0 0' },
  primaryBtn:     { padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  tabs:           { display: 'flex', gap: 4, marginBottom: 20, background: '#fff', padding: 4, borderRadius: 8, width: 'fit-content', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', flexWrap: 'wrap' },
  tab:            { padding: '6px 14px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer', background: 'transparent', color: '#64748b' },
  tabActive:      { background: '#2563eb', color: '#fff' },
  body:           { display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' },
  formCard:       { background: '#fff', borderRadius: 10, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  formTitle:      { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 16px' },
  form:           { display: 'flex', flexDirection: 'column', gap: 14 },
  formGrid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 },
  field:          { display: 'flex', flexDirection: 'column', gap: 5 },
  label:          { fontSize: 13, fontWeight: 600, color: '#374151' },
  input:          { padding: '8px 11px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  formActions:    { display: 'flex', justifyContent: 'flex-end', gap: 10 },
  cancelBtn:      { padding: '9px 16px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  empty:          { background: '#fff', borderRadius: 10, padding: 40, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  emptyText:      { color: '#94a3b8', fontSize: 14 },
  taskList:       { display: 'flex', flexDirection: 'column', gap: 10 },
  taskRow:        { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer', transition: 'background 0.15s' },
  taskMain:       { flex: 1, minWidth: 0 },
  taskTop:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  taskName:       { fontSize: 14, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 300 },
  taskBadges:     { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  taskBottom:     { display: 'flex', alignItems: 'center', gap: 10 },
  progressSlider: { flex: 1, maxWidth: 200, height: 6, borderRadius: 99, outline: 'none', cursor: 'pointer', WebkitAppearance: 'none', appearance: 'none' },
  progressPct:    { fontSize: 12, fontWeight: 600, color: '#64748b', minWidth: 32 },
  dueDate:        { fontSize: 12, fontWeight: 500, marginLeft: 'auto' },
  taskActions:    { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  slider:         { width: 80, cursor: 'pointer', accentColor: '#2563eb' },
  badge:          { padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' },
  healthDot:      { width: 10, height: 10, borderRadius: '50%', display: 'inline-block' },
  iconBtn:        { width: 26, height: 26, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  notesPanel:     { width: 320, flexShrink: 0, background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 16, minWidth: 280, maxWidth: '100%' },
  notesPanelHeader:{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  notesPanelTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 },
  notesPanelSub:   { fontSize: 12, color: '#64748b', margin: '4px 0 0', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  closeBtn:       { background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#94a3b8' },
  notesList:      { display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 200, overflowY: 'auto' },
  noNotes:        { color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '16px 0' },
  noteItem:       { background: '#f8fafc', borderRadius: 8, padding: 12 },
  noteAuthorRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  noteAuthor:     { fontSize: 12, fontWeight: 700, color: '#2563eb' },
  noteDate:       { fontSize: 11, color: '#94a3b8' },
  noteHours:      { fontSize: 11, color: '#2563eb', fontWeight: 600, display: 'block', marginBottom: 4 },
  noteContent:    { fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.5 },
  noteForm:       { display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid #f1f5f9', paddingTop: 16 },
  addNoteTitle:   { fontSize: 13, fontWeight: 700, color: '#0f172a', margin: 0 },
  hoursLabel:     { fontSize: 12, color: '#64748b' },
  legend:         { display: 'flex', gap: 10, marginTop: 24, padding: '12px 16px', background: '#fff', borderRadius: 8, width: 'fit-content', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', flexWrap: 'wrap', alignItems: 'center' },
  legendItem:     { display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 7, cursor: 'pointer', background: 'transparent' },
  legendDot:      { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  legendLabel:    { fontSize: 12, color: '#64748b', fontWeight: 500 },
  legendClear:    { fontSize: 12, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: '5px 8px' },
  startedBy:      { fontSize: 11, color: '#64748b', marginBottom: 4 },
  timerBox:       { display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: '#f0fdf4', borderRadius: 99, border: '1px solid #bbf7d0' },
  timerDot:       { width: 7, height: 7, borderRadius: '50%', background: '#16a34a', flexShrink: 0 },
  timerText:      { fontSize: 12, fontWeight: 700, color: '#16a34a', fontFamily: 'monospace' },
  miniAvatar:     { width: 22, height: 22, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 },
  assignedBadge:  { fontSize: 11, fontWeight: 600, color: '#374151', background: '#f1f5f9', padding: '3px 8px', borderRadius: 99, whiteSpace: 'nowrap' },
  assignedAvatars:{ display: 'flex', gap: 2, alignItems: 'center' },
  modalOverlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:          { background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  aiHeader:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  aiTitle:        { fontSize: 13, fontWeight: 700, color: '#7c3aed' },
  aiAnalyzeBtn:   { padding: '4px 12px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  aiAnalysis:     { background: '#faf5ff', borderRadius: 7, padding: 10, marginBottom: 8, border: '1px solid #e9d5ff', maxHeight: 180, overflowY: 'auto' },
  aiAnalysisText: { fontSize: 11, color: '#374151', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: 1.6 },
  aiChat:         { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8, maxHeight: 150, overflowY: 'auto' },
  aiChatMsg:      { padding: '6px 10px', borderRadius: 8, fontSize: 12, lineHeight: 1.5, maxWidth: '90%' },
  aiQuestionForm: { display: 'flex', gap: 6 },
  aiSendBtn:      { padding: '6px 12px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer', fontWeight: 700 },
};